
/**
 * Module dependencies.
 */

import {
  FormControl,
  FormGroup,
  HelpText,
  Label
} from 'src/components/core/forms/fields-styled-components';

import { units } from 'src/styles/utils';
import { useTranslate } from 'src/core/utils/translator';
import IconButton from 'src/components/core/buttons/icon-button';
import React, { ReactElement, forwardRef, useState } from 'react';
import eyeIcon from 'src/assets/svg/eye.svg';
import eyeOffIcon from 'src/assets/svg/eye-off.svg';
import isEmpty from 'lodash/isEmpty';
import styled from 'styled-components';

/**
 * `Error` type.
 */

type Error = {
  message: string,
  type: string
};

/**
 * `Props` type.
 */

type Props = {
  as?: any,
  className?: string,
  disabled?: boolean,
  error?: Error | null | undefined,
  helpText?: string,
  id?: string,
  label: string,
  name: string,
  placeholder: string,
};

/**
 * `Wrapper` styled component.
 */

const Wrapper = styled.div`
  align-items: center;
  display: flex;
  position: relative;
`;

/**
 * `Icon` styled component.
 */

const Icon = styled(IconButton)`
  position: absolute;
  right: 2px;
  text-decoration: none;
  top: ${units(3)};
  width: 24px;
`;

/**
 * `Input` component.
 */

const Input = (props: Props, ref: any): ReactElement => {
  const {
    className,
    disabled,
    error,
    helpText,
    id,
    label,
    name,
    ...rest
  } = props;

  const { translate } = useTranslate();
  const hasError = !isEmpty(error);
  const [showPassword, setShowPassword] = useState<boolean>();

  return (
    <FormGroup
      className={className}
      disabled={disabled}
    >
      <Label htmlFor={id ?? name}>
        {label}
      </Label>

      <Wrapper>
        <FormControl
          hasError={hasError}
          id={id ?? name}
          name={name}
          ref={ref}
          type={showPassword ? 'text' : 'password'}
          {...rest}
        />

        <Icon
          aria-label={translate(showPassword ? 'forms:hidePassword' : 'forms:showPassword')}
          icon={showPassword ? eyeIcon : eyeOffIcon}
          iconSize={units(3)}
          onClick={() => setShowPassword(!showPassword)}
        />
      </Wrapper>

      {hasError && (
        <HelpText hasError>
          {error?.message}
        </HelpText>
      )}

      {helpText && !hasError && (
        <HelpText>
          {helpText}
        </HelpText>
      )}
    </FormGroup>
  );
};

/**
 * Export `Input` component.
 */

export default forwardRef<Props, any>(Input);
